import { useState, useEffect } from 'react';

export interface DocSection {
  id: string;
  title: string;
  content: string;
  category: string;
  fileName: string;
}

// Define the documentation structure and categories
export const documentationIndex = [
  {
    fileName: 'faq.md',
    id: 'faq',
    title: 'Frequently Asked Questions',
    category: 'Support'
  },
  {
    fileName: 'usdp-whitepaper.md',
    id: 'whitepaper',
    title: 'USD.P Whitepaper',
    category: 'Getting Started'
  },
  {
    fileName: 'technical-overview.md',
    id: 'technical-overview',
    title: 'Technical Overview',
    category: 'Technical'
  },
  {
    fileName: 'smart-contract.md',
    id: 'smart-contract',
    title: 'Smart Contract Addresses',
    category: 'Technical'
  },
  {
    fileName: 'minting-and-redeeming.md',
    id: 'minting-redeeming',
    title: 'Minting & Redeeming',
    category: 'Getting Started'
  },
  {
    fileName: 'token-features-usdp.md',
    id: 'token-features-usdp',
    title: 'USD.P Token Features',
    category: 'Token Features'
  },
  {
    fileName: 'multi-chain-networks.md',
    id: 'multi-chain-networks',
    title: 'Multi-Chain Networks',
    category: 'Technical'
  },
  {
    fileName: 'how-to-display-usdp-token-in-wallet.md',
    id: 'wallet-display',
    title: 'How to Display USD.P in Wallet',
    category: 'Getting Started'
  },
  {
    fileName: 'collateral-and-lending.md',
    id: 'collateral-lending',
    title: 'Collateral & Lending',
    category: 'Technical'
  },
  {
    fileName: 'what-is-e-pay.md',
    id: 'what-is-e-pay',
    title: 'What is E-Pay',
    category: 'Support'
  },
  {
    fileName: 'yield-generation.md',
    id: 'yield-generation',
    title: 'Yield Generation',
    category: 'Technical'
  },
  {
    fileName: 'security-and-audits.md',
    id: 'security-audits',
    title: 'Security and Audits',
    category: 'Legal'
  },
  {
    fileName: 'terms-of-use.md',
    id: 'terms-of-use',
    title: 'Terms of Use',
    category: 'Legal'
  },
  {
    fileName: 'user-guide.md',
    id: 'user-guide',
    title: 'User Guide',
    category: 'Getting Started'
  },
  {
    fileName: 'api-docs.md',
    id: 'api-docs',
    title: 'API Documentation',
    category: 'Technical'
  }
];

export const categories = [
  'Getting Started',
  'Token Features',
  'Technical',
  'Legal',
  'Support'
];

export const useDocumentationLoader = () => {
  const [docSections, setDocSections] = useState<DocSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDocumentation = async () => {
      try {
        setLoading(true);
        const loadedSections: DocSection[] = [];

        // Load each documentation file
        for (const docConfig of documentationIndex) {
          try {
            const response = await fetch(`/usdp-documentation/${docConfig.fileName}`);
            if (response.ok) {
              const content = await response.text();
              loadedSections.push({
                id: docConfig.id,
                title: docConfig.title,
                content: content,
                category: docConfig.category,
                fileName: docConfig.fileName
              });
            } else {
              console.warn(`Failed to load ${docConfig.fileName}: ${response.status}`);
            }
          } catch (fileError) {
            console.warn(`Error loading ${docConfig.fileName}:`, fileError);
          }
        }

        setDocSections(loadedSections);
        setError(null);
      } catch (err) {
        setError('Failed to load documentation');
        console.error('Documentation loading error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDocumentation();
  }, []);

  return { docSections, loading, error };
};

export const extractTitle = (markdown: string): string => {
  const titleMatch = markdown.match(/^#\s+(.+)$/m);
  return titleMatch ? titleMatch[1] : 'Untitled Document';
};

export const cleanMarkdownForDisplay = (markdown: string): string => {
  // Remove frontmatter if present
  const withoutFrontmatter = markdown.replace(/^---[\s\S]*?---\n/, '');
  
  // Clean up GitBook specific syntax
  let cleaned = withoutFrontmatter
    .replace(/{% content-ref url="[^"]*" %}\n\[[^\]]*\]\([^)]*\)\n{% endcontent-ref %}/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\s+"mention"\)/g, '$1')
    .replace(/<figure><img[^>]*><figcaption[^>]*><\/figcaption><\/figure>/g, '')
    .replace(/<figure><img[^>]*><figcaption><\/figcaption><\/figure>/g, '');

  // Convert PDF object tags to div tags with special attributes that we can detect
  cleaned = cleaned.replace(
    /<object\s+data="([^"]+\.pdf[^"]*)"[^>]*aria-label="([^"]*)"[^>]*>[\s\S]*?<\/object>/gi,
    (match, pdfUrl, ariaLabel) => {
      return `<div data-pdf-viewer="true" data-pdf-src="${pdfUrl}" data-pdf-title="${ariaLabel || 'Document'}"></div>`;
    }
  );
  
  return cleaned.trim();
};